<template>
  <form
    class="report-background"
    @click.stop=""
    @submit="submitFeedback"
    ref="formRef"
  >
    <div class="flex w-full items-center justify-between">
      <p
        v-if="!isSuccess"
        class="text-2xl font-bold text-primary"
      >
        Send Feedback!
      </p>
      <IconButton
        icon="times"
        @click="emits('close')"
        class="ml-auto text-primary"
        size="lg"
      />
    </div>
    <div
      v-if="isSuccess"
      class="flex h-[428px] flex-col items-center justify-center gap-y-8 p-8 text-center"
    >
      <img
        :src="IMAGE_BASE_URL + '/character/feedback/submitted.png'"
        alt="feedback submitted"
        width="192"
      >
      <div class="flex flex-col items-center gap-y-2">
        <p class="text-2xl font-bold text-primary">
          Thank you!
        </p>
        <p class="text-lg text-primary/40">
          Your feedback has been successfully submitted.
        </p>
      </div>
    </div>
    <template v-else>
      <p class="flex flex-col gap-y-2 text-lg text-primary">
        You can help improve Maplestory Universe Explorer by giving us feedback on any issues or suggestions you have.
        <span class="text-primary/40">* Indicates required field.</span>
      </p>
      <ErrorMessageWrapper :error-message="errors.type">
        <BcSelect
          v-model="type"
          :options="feedbackTypeList"
          class="w-full bg-primary/10 text-lg"
          placeholder="Select Feedback Type *"
          name="feedbackType"
        />
      </ErrorMessageWrapper>
      <ErrorMessageWrapper :error-message="errors.comment">
        <textarea
          v-model="comment"
          name="feedbackComment"
          class="comment-area"
          placeholder="Comments *"
        />
      </ErrorMessageWrapper>
      <div class="flex w-full flex-col gap-y-1">
        <label
          for="feedbackUrl"
          class="flex w-full flex-col gap-y-2 text-primary"
        >
          <span class="flex items-center justify-between text-base">
            <span class="flex items-center gap-x-1">URL</span>
            <button
              class="text-sm text-primary underline"
              type="button"
              @click="setFieldValue('url', currentUrl)"
            >INSERT CURRENT PAGE URL</button>
          </span>
          <ExInput
            v-model="url"
            placeholder="Enter URL"
            class="url-input"
            name="feedbackUrl"
          />
        </label>
      </div>
      <button
        class="w-full rounded-lg bg-point-color p-2 text-primary transition-all duration-300 hover:bg-point-color disabled:opacity-40"
      >
        Send
      </button>
    </template>
  </form>
</template>

<script setup lang="ts">
import { object, string } from 'yup'

import { CommonFeedbackRequest } from '~/api/repository'
import IconButton from '~/components/button/IconButton.vue'
import { type ITab } from '~/types/tab.type'
import ErrorMessageWrapper from '~/ui/Form/ErrorMessageWrapper.vue'
import ExInput from '~/ui/Input/ExInput.vue'
import BcSelect from '~/ui/Select/BcSelect.vue'
import feedbackType = CommonFeedbackRequest.feedbackType;
import { toTypedSchema } from '@vee-validate/yup'
import { useForm } from 'vee-validate'

import { feedbackService } from '~/api/service/feedback.service'
import { FEEDBACK_STORAGE, INITIAL_FEEDBACK_FORM } from '~/constants/feedback.constants'
import { IMAGE_BASE_URL } from '~/constants/image.constant'
import { type IFeedbackForm } from '~/types/feedback.type'

const emits = defineEmits([ 'close' ])

const route = useRoute()
const { mutate, isSuccess } = feedbackService.useCreateFeedback()

const feedbackValidationSchema = toTypedSchema(
  object({
    type: string().required('Select feedback type.'),
    comment: string().trim().required('Enter a comment.'),
  })
)

const { errors, setFieldValue, handleSubmit, defineField, resetForm } = useForm<IFeedbackForm>({
  initialValues: FEEDBACK_STORAGE.value,
  validationSchema: feedbackValidationSchema,
})

const feedbackTypeList: Array<ITab<AptosFeedbackRequest.feedbackType>> = Object.values(feedbackType).map(type => {
  return {
    label: capitalizeFirstLetterWithDelimiter(type, '_'),
    value: type,
  }
})

const formRef = ref<HTMLFormElement | null>(null)
const formScrollHeight = computed(() => {
  return formRef.value?.scrollHeight + 'px'
})
const currentUrl = computed(() => import.meta.env.VITE_APP_BASE_URL + route.fullPath)

const [ type ] = defineField('type')
const [ comment ] = defineField('comment')
const [ url ] = defineField('url')

const submitFeedback = handleSubmit(values => {
  mutate(values, {
    onSuccess: () => {
      FEEDBACK_STORAGE.value = INITIAL_FEEDBACK_FORM
      resetForm({
        values: INITIAL_FEEDBACK_FORM,
      })
    }
  })
})

onBeforeUnmount(() => {
  FEEDBACK_STORAGE.value = {
    type: type.value,
    comment: comment.value,
    url: url.value,
  }
})
</script>

<style scoped lang="scss">
.report-background {
  @apply absolute bottom-[calc(100%+0.5rem)] right-6 flex w-[350px] cursor-default flex-col items-center gap-y-4
  rounded-lg p-8 text-left box-content overflow-y-auto h-fit bg-black/40;
  max-height: calc(100vh - 12rem);

  &::before {
    @apply absolute top-0 left-0 w-full rounded-lg;
    @include background-blur(64px);
    content: "";
    z-index: -1;
    height: max(100%, v-bind(formScrollHeight));
  }

  &::after {
    @apply absolute top-0 left-0 w-full rounded-lg bg-gradient-to-r from-primary/[16%] to-point-color/40;
    content: "";
    z-index: -1;
    height: max(100%, v-bind(formScrollHeight));
  }

  @include sm {
    @apply box-border w-[calc(100vw-2rem)] p-4 right-4;
  }

  @include custom-scrollbar;
}

.comment-area {
  @apply bg-primary/10 rounded-lg p-2 text-primary text-lg w-full h-32 outline-0 placeholder:text-primary/40;
  resize: none;

  &::-webkit-resizer {
    display: none;
  }

  @include custom-scrollbar;
}

.url-input {
  @apply rounded-lg bg-primary/10 p-2 text-primary;

  :deep(.default-input) {
    @apply text-lg placeholder:text-primary/40;
  }
}
</style>